/**
 * Venchy Assistant Component
 * 
 * Main floating chat assistant that helps manage appointments.
 * Features:
 * - Floating toggle button with pulse animation
 * - Chat panel with messages
 * - Quick action buttons
 * - Typing indicator
 * - Welcome screen with examples
 * - Action cards for appointments
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import { tWithKeyAttr as t } from "../../i18nHelper";
import { IChatMessage, IActionCard } from "./types/venchy.types";
import { 
  quickActionButtons, 
  suggestionItems,
  mockPendingAppointments,
  mockAvailableSlots,
  mockStatsResponse
} from "./mocks/venchy.mock";
import ChatMessage from "./components/ChatMessage";
import WelcomeScreen from "./components/WelcomeScreen";
import TypingIndicator from "./components/TypingIndicator";
import styles from "./venchy.module.css";

/**
 * VenchyAssistant Component
 * 
 * Global floating chat assistant for appointment management.
 */
const VenchyAssistant: React.FC = () => {
  // State
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Refs
  const chatAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  
  // Mock user data - in real app, this comes from auth context
  const userName = "יעל";
  const userInitial = "י";

  // Scroll to bottom when messages change
  useEffect(() => {
    if (chatAreaRef.current) {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Generate unique ID
  const generateId = () => `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Add a message to the chat
  const addMessage = useCallback((content: string, sender: 'user' | 'assistant', actionCard?: IActionCard) => {
    const newMessage: IChatMessage = {
      id: generateId(),
      sender,
      content,
      timestamp: new Date(),
      actionCard
    };
    setMessages(prev => [...prev, newMessage]);
  }, []);

  // Handle AI response based on user message
  const handleAIResponse = useCallback((userMessage: string) => {
    const lowerMessage = userMessage.toLowerCase();

    // Check for pending/approval keywords
    if (lowerMessage.includes('ממתין') || lowerMessage.includes('אישור') || 
        lowerMessage.includes('pending') || lowerMessage.includes('approval')) {
      addMessage(t("VENCHY_RESPONSE_PENDING_FOUND", { count: mockPendingAppointments.length }), 'assistant');
      
      // Add action cards with delays
      mockPendingAppointments.forEach((apt, index) => {
        setTimeout(() => {
          addMessage('', 'assistant', apt);
        }, (index + 1) * 300);
      });
    }
    // Check for search/available keywords
    else if (lowerMessage.includes('מצא') || lowerMessage.includes('פנוי') ||
             lowerMessage.includes('find') || lowerMessage.includes('available')) {
      addMessage(t("VENCHY_RESPONSE_SLOTS_FOUND", { count: mockAvailableSlots.length }), 'assistant');
      
      mockAvailableSlots.forEach((slot, index) => {
        setTimeout(() => {
          addMessage('', 'assistant', slot);
        }, (index + 1) * 300);
      });
    }
    // Check for stats/statistics keywords
    else if (lowerMessage.includes('כמה') || lowerMessage.includes('סטטיסטיק') ||
             lowerMessage.includes('how many') || lowerMessage.includes('statistic')) {
      addMessage(t("VENCHY_RESPONSE_STATS_INTRO"), 'assistant');
      setTimeout(() => {
        addMessage(t("VENCHY_RESPONSE_STATS_WEEKLY", {
          total: mockStatsResponse.totalAppointments,
          approved: mockStatsResponse.approved,
          approvedPercent: mockStatsResponse.approvedPercent,
          pending: mockStatsResponse.pending,
          cancelled: mockStatsResponse.cancelled,
          busiestTherapist: mockStatsResponse.busiestTherapist,
          busiestCount: mockStatsResponse.busiestTherapistCount,
          dominantInsurance: mockStatsResponse.dominantInsurance,
          dominantPercent: mockStatsResponse.dominantInsurancePercent
        }), 'assistant');
      }, 500);
    }
    // Check for move/transfer keywords
    else if (lowerMessage.includes('העבר') || lowerMessage.includes('שנה') ||
             lowerMessage.includes('move') || lowerMessage.includes('transfer')) {
      addMessage(t("VENCHY_RESPONSE_MOVE_HELP"), 'assistant');
    }
    // Default response
    else {
      addMessage(t("VENCHY_RESPONSE_DEFAULT"), 'assistant');
    }
  }, [addMessage]);

  // Send message
  const sendMessage = useCallback(() => {
    const message = inputValue.trim();
    if (!message) return;

    // Add user message
    addMessage(message, 'user');
    
    // Clear input
    setInputValue("");
    setShowSuggestions(false);
    
    // Show typing indicator
    setIsTyping(true);
    
    // Simulate AI response after delay
    setTimeout(() => {
      setIsTyping(false);
      handleAIResponse(message);
    }, 1500);
  }, [inputValue, addMessage, handleAIResponse]);

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    // Show suggestions when typing and no messages yet
    if (e.target.value.length > 0 && messages.length === 0) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  // Handle quick action
  const handleQuickAction = (promptKey: string) => {
    setInputValue(t(promptKey));
    inputRef.current?.focus();
  };

  // Handle suggestion click
  const handleSuggestionClick = (prompt: string) => {
    setInputValue(prompt);
    setShowSuggestions(false);
    // Auto-send
    setTimeout(() => {
      addMessage(prompt, 'user');
      setInputValue("");
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        handleAIResponse(prompt);
      }, 1500);
    }, 100);
  };

  // Handle example click from welcome screen
  const handleExampleClick = (prompt: string) => {
    addMessage(prompt, 'user');
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      handleAIResponse(prompt);
    }, 1500);
  };

  // Handle approve action
  const handleApprove = (id: string) => {
    addMessage(t("VENCHY_RESPONSE_APPROVED"), 'assistant');
  };

  // Handle reject action
  const handleReject = (id: string) => {
    addMessage(t("VENCHY_RESPONSE_REJECTED"), 'assistant');
  };

  // Toggle panel
  const togglePanel = () => {
    setIsOpen(prev => !prev);
  };

  return (
    <>
      {/* Toggle Button */}
      <button 
        className={`${styles.venchyToggle} ${isOpen ? styles.active : ''}`}
        onClick={togglePanel}
        aria-label="Open Venchy Assistant"
      >
        🤖
      </button>

      {/* Chat Panel */}
      <div className={`${styles.venchyPanel} ${isOpen ? styles.active : ''}`}>
        {/* Panel Header */}
        <div className={styles.venchyPanelHeader}>
          <div className={styles.venchyPanelTitle}>
            <div className={styles.venchyAvatar}>🤖</div>
            <span>{t("VENCHY_PANEL_TITLE")}</span>
          </div>
          <button 
            className={styles.venchyCloseBtn}
            onClick={() => setIsOpen(false)}
            aria-label="Close panel"
          >
            ×
          </button>
        </div>

        {/* Quick Actions */}
        <div className={styles.venchyQuickActions}>
          {quickActionButtons.map(action => (
            <button
              key={action.id}
              className={styles.venchyQuickActionBtn}
              onClick={() => handleQuickAction(action.promptKey)}
            >
              <span>{action.icon}</span>
              <span>{t(action.labelKey)}</span>
            </button>
          ))}
        </div>

        {/* Suggestions */}
        <div className={`${styles.venchySuggestions} ${showSuggestions ? styles.active : ''}`}>
          {suggestionItems.map(suggestion => (
            <div
              key={suggestion.id}
              className={styles.venchySuggestionItem}
              onClick={() => handleSuggestionClick(t(suggestion.prompt))}
            >
              <span className={styles.venchySuggestionIcon}>{suggestion.icon}</span>
              <span>{t(suggestion.textKey)}</span>
            </div>
          ))}
        </div>

        {/* Chat Area */}
        <div className={styles.venchyChatArea} ref={chatAreaRef}>
          {messages.length === 0 ? (
            <WelcomeScreen 
              userName={userName}
              onExampleClick={handleExampleClick}
            />
          ) : (
            <>
              {messages.map(message => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  userInitial={userInitial}
                  onApprove={handleApprove}
                  onReject={handleReject}
                />
              ))}
              <TypingIndicator isActive={isTyping} />
            </>
          )}
        </div>

        {/* Input Area */}
        <div className={styles.venchyInputArea}>
          <div className={styles.venchyInputContainer}>
            <div className={styles.venchyInputWrapper}>
              <textarea
                ref={inputRef}
                className={styles.venchyChatInput}
                placeholder={t("VENCHY_INPUT_PLACEHOLDER")}
                value={inputValue}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                rows={1}
              />
            </div>
            <button
              className={styles.venchySendBtn}
              onClick={sendMessage}
              disabled={!inputValue.trim()}
              aria-label="Send message"
            >
              ➤
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default VenchyAssistant;
